/**
 * Find and replace, over the ProseMirror document.
 *
 * SEARCHED OVER THE DOCUMENT, NOT THE DOM. The editor renders what is on screen; a long procedure
 * is not all on screen, and `window.find` would miss everything below the fold and anything inside
 * a collapsed node. Walking the document's own text nodes finds every occurrence and gives
 * positions the editor can actually select.
 *
 * REPLACE IS A TRANSACTION, WHICH IS WHY IT SURVIVES COLLABORATION. Replacing all through the
 * editor's own commands means every change is a CRDT update like any other: it reaches the other
 * windows, it is undoable, and it cannot desynchronise two people's copies the way a direct
 * mutation of the text would.
 */
import React from "react";
import { Box, Button, IconButton, InputBase, Paper, Tooltip, Typography } from "@mui/material";
import { Close, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";

import { Grey3, Grey5, Grey17 } from "../../theme/styles";

type Hit = { from: number; to: number };

export default function FindReplace({ editor, editable, onClose }: {
    editor: any; editable: boolean; onClose: () => void;
}) {
    const [term, setTerm] = React.useState("");
    const [replacement, setReplacement] = React.useState("");
    const [at, setAt] = React.useState(0);

    const hits: Hit[] = React.useMemo(() => {
        if (!editor || term.length < 1) return [];
        const found: Hit[] = [];
        const needle = term.toLowerCase();
        editor.state.doc.descendants((node: any, pos: number) => {
            if (!node.isText || !node.text) return;
            const haystack = node.text.toLowerCase();
            let index = haystack.indexOf(needle);
            while (index !== -1) {
                found.push({ from: pos + index, to: pos + index + term.length });
                index = haystack.indexOf(needle, index + 1);
            }
        });
        return found;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, term, editor?.state?.doc]);

    const go = (next: number) => {
        if (hits.length === 0) return;
        const index = ((next % hits.length) + hits.length) % hits.length;
        setAt(index);
        const hit = hits[index];
        editor.chain().focus().setTextSelection({ from: hit.from, to: hit.to })
              .scrollIntoView().run();
    };

    const replaceOne = () => {
        if (!hits.length || !editable) return;
        const hit = hits[Math.min(at, hits.length - 1)];
        editor.chain().focus().insertContentAt({ from: hit.from, to: hit.to }, replacement).run();
    };

    const replaceAll = () => {
        if (!hits.length || !editable) return;
        // BACKWARDS, because replacing shifts every position after it. Going forwards with stale
        // positions corrupts the text in exactly the way that is hardest to notice: the first few
        // replacements look right.
        const chain = editor.chain().focus();
        [...hits].reverse().forEach(hit =>
            chain.insertContentAt({ from: hit.from, to: hit.to }, replacement));
        chain.run();
    };

    return (
        <Paper elevation={3} sx={{ position: "fixed", top: 96, right: 28, zIndex: 1200,
                                   p: 1, width: 330, borderRadius: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <InputBase autoFocus value={term} placeholder="Find in document"
                           onChange={(e) => { setTerm(e.target.value); setAt(0); }}
                           onKeyDown={(e) => { if (e.key === "Enter") go(at + 1);
                                               if (e.key === "Escape") onClose(); }}
                           inputProps={{ "aria-label": "Find in document" }}
                           sx={{ flexGrow: 1, fontSize: 13, border: `1px solid ${Grey3}`,
                                 borderRadius: 1, px: 1, py: 0.25 }}/>
                <Typography sx={{ fontSize: 12, color: Grey5, minWidth: 54, textAlign: "right" }}>
                    {term ? `${hits.length ? at + 1 : 0} of ${hits.length}` : ""}
                </Typography>
                <Tooltip title="Previous"><span>
                    <IconButton size="small" disabled={!hits.length} onClick={() => go(at - 1)}
                                aria-label="Previous match"><KeyboardArrowUp fontSize="small"/></IconButton>
                </span></Tooltip>
                <Tooltip title="Next"><span>
                    <IconButton size="small" disabled={!hits.length} onClick={() => go(at + 1)}
                                aria-label="Next match"><KeyboardArrowDown fontSize="small"/></IconButton>
                </span></Tooltip>
                <IconButton size="small" onClick={onClose} aria-label="Close find"><Close fontSize="small"/></IconButton>
            </Box>

            {/* REPLACE IS HIDDEN ON A CLOSED REVISION rather than disabled, because the whole row
                would otherwise sit there inviting a change that cannot be made to a released
                document. Find still works: reading a released procedure is most of what anybody
                does with one. */}
            {editable && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75 }}>
                    <InputBase value={replacement} placeholder="Replace with"
                               onChange={(e) => setReplacement(e.target.value)}
                               inputProps={{ "aria-label": "Replace with" }}
                               sx={{ flexGrow: 1, fontSize: 13, border: `1px solid ${Grey3}`,
                                     borderRadius: 1, px: 1, py: 0.25 }}/>
                    <Button size="small" disabled={!hits.length} onClick={replaceOne}
                            sx={{ fontSize: 12, color: Grey17 }}>Replace</Button>
                    <Button size="small" disabled={!hits.length} onClick={replaceAll}
                            sx={{ fontSize: 12, color: Grey17 }}>All</Button>
                </Box>
            )}
        </Paper>
    );
}
